import { useQuery, useReactiveVar } from '@apollo/client';
import {
  AnimationClassNames,
  PrimaryButton,
  Stack
} from '@fluentui/react';
import { GetApprovalCommonData } from 'approvals/__generated__/GetApprovalCommonData';
import {
  ApprovalHistory_approvalHistory__budgetStatus
} from 'approvals/ApprovalDetails/__generated__/ApprovalHistory';
import { ApprovalHistoryDetailComponent } from 'approvals/ApprovalDetails/ApprovalHistoryDetailComponent';
import { ApprovalHistoryHeader } from 'approvals/ApprovalDetails/ApprovalHistoryHeader';
import { BudgetStatusChart } from 'approvals/ApprovalDetails/budgetStatusChart';
import { useApprovalHistoryLazyQuery } from 'approvals/ApprovalDetails/hooks/useApprovalHistoryLazyQuery';
import { ReplaceRemoveApprover } from 'approvals/ApprovalDetails/ReplaceRemoveApprover';
import { ShimmerView } from 'approvals/ApprovalDetails/ShimmerView/ShimmerViews';
import { getApprovalOrderBy } from 'approvals/ApprovalDetails/utils';
import { setApprovalLayoutType } from 'approvals/MyApprovals';
import { HierarchyModal } from 'common/components/HierarchyModal';
import { ApprovalHistoryItemsOrderBy } from 'common/types/globalTypes';
import { useCallback, useEffect, useState } from 'react';
import COMMON_DATA from '../../../../../../approvals/GetApprovalCommonData.graphql';
import { useStyles } from './index.styles';

interface ViewTransactionApprovalDetailsProps {
  listMode: boolean;
  selectedItem: string;
}

export const ViewTransactionApprovalDetails: React.FC<
  ViewTransactionApprovalDetailsProps
> = ({ listMode, selectedItem }) => {
  const SlideLeftIn = AnimationClassNames.slideLeftIn400;
  const slideRightOut = AnimationClassNames.slideRightOut400;
  const styles = useStyles();
  const [showEditApprover, setShowEditApprover] = useState<boolean>(false);
  const [showBudgetChart, setShowBudgetChart] = useState<boolean>(false);
  const { data: commonData } = useQuery<GetApprovalCommonData>(COMMON_DATA, {
    fetchPolicy: 'cache-first',
  });
  const {
    getApprovalHistory,
    data: approvalHistoryDetails,
    loading: approvalHistoryLoading,
    refetch: refetchApprovalHistory,
    variables,
  } = useApprovalHistoryLazyQuery();

  const getApprovalHistoryMemo = useCallback(getApprovalHistory, [
    selectedItem,
  ]);
  const approvalLayoutType = useReactiveVar(setApprovalLayoutType);

  const approvalOrderBy = getApprovalOrderBy(approvalLayoutType);
  useEffect(() => {
    if (selectedItem && selectedItem !== '') {
      getApprovalHistoryMemo({
        variables: {
          id: selectedItem!,
          orderBy: approvalOrderBy as ApprovalHistoryItemsOrderBy[],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, getApprovalHistoryMemo]);
  const chartColumns = [];
  let i = 1;

  while (
    approvalHistoryDetails?.approvalHistory?._budgetStatus?.[
      `_isColumn${i}Required` as keyof ApprovalHistory_approvalHistory__budgetStatus
    ]
  ) {
    chartColumns.push({
      columnCaption:
        approvalHistoryDetails?.approvalHistory?._budgetStatus?.[
          `_column${i}Caption` as keyof ApprovalHistory_approvalHistory__budgetStatus
        ],
      columnAmount:
        approvalHistoryDetails?.approvalHistory?._budgetStatus?.[
          `_column${i}Amount` as keyof ApprovalHistory_approvalHistory__budgetStatus
        ],
    });
    i++;
  }
  return (
    <Stack
      className={!listMode ? SlideLeftIn : slideRightOut}
      style={{
        height: 'calc(100% - 80px)',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Stack style={{ padding: 10 }}>
        <ApprovalHistoryHeader
          approvalHistoryDetails={approvalHistoryDetails}
          setShowBudgetChart={setShowBudgetChart}
        />
      </Stack>
      <Stack
        style={{
          height: 1000,
          width: '100%',
          overflow: 'auto',
          padding: '10px',
        }}
      >
        {approvalHistoryLoading ? (
          <ShimmerView />
        ) : (
          <ApprovalHistoryDetailComponent
            approvalHistoryDetails={approvalHistoryDetails}
            onRefetchApprovalHistory={refetchApprovalHistory}
            isApprovalFlow
          />
        )}
      </Stack>

      <Stack className={styles.footerContainer}>
        <Stack
          horizontal
          tokens={{ padding: 25 }}
          className={styles.footerStyle}
          horizontalAlign="space-between"
        >
          <Stack horizontal tokens={{ childrenGap: 20 }}>
            {approvalHistoryDetails?.approvalHistory
              ?.isApprovalTreeViewerAllowed && (
              <HierarchyModal
                isApprovalEntry
                entityId={approvalHistoryDetails?.approvalHistory?.id!}
              />
            )}

            {commonData?.verifyApprovalMaintenancePermission &&
              !approvalHistoryDetails?.approvalHistory?.isApproved &&
              !approvalHistoryDetails?.approvalHistory?.isRevoked &&
              !approvalHistoryDetails?.approvalHistory?.isRejected &&
              !approvalHistoryDetails?.approvalHistory?._isValidating && (
                <PrimaryButton onClick={() => setShowEditApprover(true)}>
                  Replace/Bypass Approver
                </PrimaryButton>
              )}
            {showEditApprover &&
              !!commonData?.verifyApprovalMaintenancePermission && (
                <ReplaceRemoveApprover
                  entityApprovalId={approvalHistoryDetails?.approvalHistory?.id}
                  approvalType={
                    approvalHistoryDetails?.approvalHistory?.approvalType
                      ?.approvalType
                  }
                  iconType={approvalHistoryDetails?.approvalHistory?.iconType}
                  onDismiss={() => setShowEditApprover(false)}
                />
              )}
          </Stack>
        </Stack>
      </Stack>
      {showBudgetChart && (
        <BudgetStatusChart
          chartColumns={chartColumns}
          onDismiss={() => setShowBudgetChart(false)}
          title={
            approvalHistoryDetails?.approvalHistory?._budgetStatus?._caption ??
            'Budget Status'
          }
          isOverBudget={
            !!approvalHistoryDetails?.approvalHistory?._budgetStatus
              ?._isOverBudget
          }
        />
      )}
    </Stack>
  );
};
